import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

// Import background images
import bg1 from "../assets/images/hero/1.jpg";
import bg2 from "../assets/images/hero/2.jpg";
import bg3 from "../assets/images/hero/3.jpg";
import bg4 from "../assets/images/hero/4.jpg";

const HeroSection = () => {
  const navigate = useNavigate();
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const slides = [
    {
      title: "Intelligent full process solution for engineering construction",
      subtitle: "A service provider of intelligent full process solutions for engineering construction, helping customers improve the level of intelligent management of large-scale oil and gas ground engineering at home and abroad",
      backgroundImage: bg1,
      overlay: "linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.6) 100%)"
    },
    {
      title: "Industry Process Digitalization & Automation",
      subtitle: "Transforming traditional processes through cutting-edge digital solutions and AI-powered automation",
      features: [
        "Digitalization",
        "Automation using AI", 
        "Smart IoT Devices"
      ],
      backgroundImage: bg2,
      overlay: "linear-gradient(135deg, rgba(230, 147, 0, 0.6) 0%, rgba(230, 147, 0, 0.4) 50%, rgba(230, 147, 0, 0.6) 100%)"
    },
    {
      title: "Smart Solutions for EPC Projects",
      subtitle: "Comprehensive AI-driven solutions for Engineering, Procurement & Construction projects",
      features: [
        "Industry AI Middle Platform - Risk Identification & Behavior Analysis",
        "Project Management Solutions - Smart Database, 3D Design, Reporting",
        "Master Data Management & Data Governance",
        "Advanced Data Security Systems"
      ],
      backgroundImage: bg3,
      overlay: "linear-gradient(135deg, rgba(0, 100, 230, 0.6) 0%, rgba(0, 100, 230, 0.4) 50%, rgba(0, 100, 230, 0.6) 100%)"
    },
    {
      title: "Integrated Smart Management Systems",
      subtitle: "End-to-end intelligent management solutions for modern industrial operations",
      features: [
        "AI Integrated Smart Warehouse Management",
        "Procurement & Logistics Optimization", 
        "Smart Camp Management with 24/7 Surveillance",
        "Vehicle Monitoring & Remote Management using AI & IoT",
        "Barcode and RFID Solutions"
      ],
      backgroundImage: bg4,
      overlay: "linear-gradient(135deg, rgba(0, 150, 100, 0.6) 0%, rgba(0, 150, 100, 0.4) 50%, rgba(0, 150, 100, 0.6) 100%)"
    }
  ];

  // Carousel responsive configuration
  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 1
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 1
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 1
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1
    }
  };

  // Preload images
  useEffect(() => {
    const loadImages = async () => {
      try {
        const imagePromises = slides.map(slide => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = slide.backgroundImage;
            img.onload = resolve;
            img.onerror = reject;
          });
        });

        await Promise.all(imagePromises);
        setImagesLoaded(true);
      } catch (error) {
        console.error("Error loading images:", error);
        setImagesLoaded(true); // Continue even if some images fail
      }
    };

    loadImages();
  }, []);

  const handleExploreSolutions = () => {
    if (window.location.pathname === "/") {
      const element = document.getElementById("achievements");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.location.href = "/#achievements";
    }
  };

  const handleContactUs = () => {
    if (window.location.pathname === "/") {
      const element = document.getElementById("contact");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.location.href = "/#contact";
    }
  };

  const handleScrollDown = () => {
    if (window.location.pathname === "/") {
      const element = document.getElementById("about");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById("about");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  // Custom arrow components
  const CustomLeftArrow = ({ onClick, ...rest }) => {
    return (
      <button 
        onClick={() => onClick()}
        className="absolute left-4 sm:left-8 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm z-20"
        aria-label="Previous slide"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
    );
  };

  const CustomRightArrow = ({ onClick, ...rest }) => {
    return (
      <button 
        onClick={() => onClick()}
        className="absolute right-4 sm:right-8 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm z-20"
        aria-label="Next slide"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    );
  };

  const CustomDot = ({ onClick, active, index, carouselState }) => {
    return (
      <button
        onClick={() => onClick()}
        className={`w-3 h-3 rounded-full transition-all duration-300 mx-1 ${
          active 
            ? "bg-orange-400 scale-125 shadow-lg" 
            : "bg-white/60 hover:bg-white/80"
        }`}
        aria-label={`Go to slide ${index + 1}`}
      />
    );
  };

  return (
    <section className="relative w-full min-h-screen flex flex-col items-center justify-center text-center overflow-hidden">
      {/* Loading State */}
      {!imagesLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-30">
          <div className="text-white text-lg">Loading...</div>
        </div>
      )}

      {/* Carousel Container */}
      <div className="relative w-full h-full">
        <Carousel
          responsive={responsive}
          infinite={true}
          autoPlay={true}
          autoPlaySpeed={5000}
          transitionDuration={700}
          containerClass="carousel-container"
          itemClass="carousel-item"
          arrows={true}
          customLeftArrow={<CustomLeftArrow />}
          customRightArrow={<CustomRightArrow />}
          showDots={true}
          customDot={<CustomDot />}
          dotListClass="custom-dot-list"
          renderDotsOutside={true}
        >
          {slides.map((slide, index) => (
            <div key={index} className="relative w-full h-screen">
              {/* Background Image with Overlay */}
              <img
                src={slide.backgroundImage}
                alt={slide.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div 
                className="absolute inset-0 w-full h-full"
                style={{
                  background: slide.overlay,
                }}
              />
              
              {/* Slide Content */}
              <div className="relative z-10 h-full flex flex-col items-center justify-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
                {/* Headline */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-6 px-4 sm:px-6 drop-shadow-lg">
                  {slide.title}
                </h1>

                {/* Subtext */}
                <p className="text-base sm:text-lg md:text-xl max-w-4xl mx-auto px-4 sm:px-6 mb-8 sm:mb-10 leading-relaxed drop-shadow-md">
                  {slide.subtitle}
                </p>

                {/* Features List (for slides 2, 3, 4) */}
                {slide.features && (
                  <div className="max-w-3xl mx-auto mb-8 sm:mb-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                      {slide.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 flex-shrink-0 shadow-md"></div>
                          <span className="text-sm sm:text-base text-white drop-shadow-md">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-12 sm:mb-16 px-4">
                  <button 
                    onClick={handleExploreSolutions}
                    className="bg-orange-500 text-white text-base sm:text-lg font-semibold px-6 sm:px-8 py-2 sm:py-3 rounded-md hover:bg-orange-600 transition w-full sm:w-auto shadow-lg hover:shadow-xl transform hover:scale-105 duration-300"
                  >
                    Explore solutions
                  </button>
                  <button 
                    onClick={handleContactUs}
                    className="border border-white text-white text-base sm:text-lg font-semibold px-6 sm:px-8 py-2 sm:py-3 rounded-md hover:bg-white/20 transition w-full sm:w-auto backdrop-blur-sm hover:backdrop-blur-md transform hover:scale-105 duration-300"
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            </div>
          ))}
        </Carousel>
      </div>

      {/* Bottom Chevron */}
      <button 
        onClick={handleScrollDown}
        className="absolute bottom-4 sm:bottom-8 text-white animate-bounce hover:text-orange-300 transition cursor-pointer z-20"
        aria-label="Scroll down to learn more"
      >
        <ChevronDown size={28} className="sm:w-8 sm:h-8 lg:w-9 lg:h-9" />
      </button>

      {/* Add some custom styles for the carousel */}
      <style jsx>{`
        .carousel-container {
          width: 100%;
          height: 100vh;
        }
        .carousel-item {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .custom-dot-list {
          position: absolute;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
        }
        :global(.react-multi-carousel-dot-list) {
          bottom: 80px;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;